import React from 'react';

interface ICSVExporterProps<T extends Record<string, unknown>> {
  onClick: () => Promise<T[]>;
  headers?: { label: string; key: string }[];
  filename?: string;
  label?: React.ReactNode;
  className?: string;
}

const CSVExporter = <T extends Record<string, unknown>>({
  onClick,
  headers,
  filename = 'export.csv',
  label,
  className,
}: ICSVExporterProps<T>) => {
  const handleExport = async () => {
    try {
      const data = await onClick();

      if (!data.length) return;

      const headerRow = headers?.map((h) => `"${h.label}"`).join(',') ||
                       Object.keys(data[0]).map((key) => `"${key}"`).join(',');

      const dataRows = data.map((row) => (
        headers
          ? headers.map((h) => `"${(row[h.key] as string) || ''}"`).join(',')
          : Object.values(row).map((val) => `"${val || ''}"`).join(',')
      ));

      const csvContent = [headerRow, ...dataRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export failed:', error);
    }
  };

  const containerStyle = { cursor: 'pointer' as const };

  return (
    <div
      onClick={handleExport}
      className={className}
      style={containerStyle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleExport();
        }
      }}
    >
      {label}
    </div>
  );
};

export default CSVExporter;
